import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';


const LoginCarousel = ({ 
  images = [], 
  autoPlay = true, 
  autoPlayInterval = 4000,
  showNavigation = true,
  showPagination = true,
  showTitle = true
}) => {
    const singleSetLength = images?.length || 0;
  const transitionDuration = 600;
  const [currentIndex, setCurrentIndex] = useState(singleSetLength);
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoPlay);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToNext = useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex(prev => {
      const nextIndex = prev + 1;
      if (nextIndex >= singleSetLength * 2) {
        setTimeout(() => {
          setIsTransitioning(false);
          setCurrentIndex(singleSetLength);
        }, 50);
        return nextIndex;
      }
      return nextIndex;
    });
  }, [singleSetLength]);

  const goToPrevious = useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex(prev => {
      const nextIndex = prev - 1;
      if (nextIndex < singleSetLength) {
        setTimeout(() => {
          setIsTransitioning(false);
          setCurrentIndex(singleSetLength * 2 - 1);
        }, 50);
        return nextIndex;
      }
      return nextIndex;
    });
  }, [singleSetLength]);

  const goToSlide = useCallback((index) => {
    setIsTransitioning(true);
    setCurrentIndex(index + singleSetLength);
  }, [singleSetLength]);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(goToNext, autoPlayInterval);
    return () => clearInterval(interval);
  }, [isAutoPlaying, autoPlayInterval, goToNext]);

  useEffect(() => {
    if (!isTransitioning) return;
    const timeout = setTimeout(() => setIsTransitioning(false), transitionDuration);
    return () => clearTimeout(timeout);
  }, [isTransitioning, transitionDuration]);

  const getActualIndex = useCallback(() => {
    if (singleSetLength === 0) return 0;
    return currentIndex % singleSetLength;
  }, [currentIndex, singleSetLength]);

  const transitionStyle = useMemo(() => isTransitioning 
    ? { transition: `transform ${transitionDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)` }
    : {}, [isTransitioning, transitionDuration]);

  const handleMouseEnter = useCallback(() => setIsAutoPlaying(false), []);
  const handleMouseLeave = useCallback(() => setIsAutoPlaying(true), []);

  if (!images || images.length === 0) {
    return null;
  }

  const extendedImages = [
    ...images,
    ...images,
    ...images
  ];

  const totalSlides = extendedImages.length;
  const currentImage = images[getActualIndex()];

  return (
    <CarouselContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <SlidesWrapper 
        $currentIndex={currentIndex} 
        $totalSlides={totalSlides}
        style={transitionStyle}
      >
        {extendedImages.map((image, index) => (
          <Slide 
            key={`slide-${index}`}
            $totalSlides={totalSlides}
          >
            <img
              src={image.src}
              alt={image.alt}
            />
          </Slide>
        ))}
      </SlidesWrapper>

      {showNavigation && (
        <>
          <NavigationButton
            $direction="left"
            onClick={goToPrevious}
            aria-label="Previous slide"
            title="Previous slide"
          />

          <NavigationButton
            $direction="right"
            onClick={goToNext}
            aria-label="Next slide"
            title="Next slide"
          />
        </>
      )}

      {showTitle && currentImage && (
        <TitleContainer>
          {currentImage.title}
        </TitleContainer>
      )}

      {showPagination && (
        <PaginationDots role="tablist" aria-label="Carousel navigation">
          {images.map((_, index) => (
            <Dot
              key={index}
              $isActive={index === getActualIndex()}
              onClick={() => goToSlide(index)}
              role="tab"
              aria-selected={index === getActualIndex()}
              aria-label={`Go to slide ${index + 1}`}
              title={`Go to slide ${index + 1}`}
            />
          ))}
        </PaginationDots>
      )}
    </CarouselContainer>
  );
};

export default LoginCarousel;

const CarouselContainer = styled.div`
  width: 100%;
  height: 480px;
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  box-shadow: var(--modal-box-shadow);
  margin-bottom: 48px;
  
  @media (min-width: 1440px) {
    height: 480px;
    margin-bottom: 100px;
  }
  
  @media (min-width: 1920px) {
    height: 580px;
    margin-bottom: 80px;
  }
  
  @media (min-width: 2560px) {
    height: 650px;
    margin-bottom: 96px;
  }
`;

const SlidesWrapper = styled.div`
  display: flex;
  width: ${props => props.$totalSlides * 100}%;
  height: 100%;
  transform: translateX(-${props => props.$currentIndex * (100 / props.$totalSlides)}%);
  transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const Slide = styled.div`
  flex: 0 0 ${props => 100 / props.$totalSlides}%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
    border-radius: 4px;
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: color-mix(in srgb, var(--color-surface-panel) 90%, transparent);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: var(--color-text-primary);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  z-index: 10;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--color-border-light);

  ${props => props.$direction === 'left' ? 'left: 24px;' : 'right: 24px;'}

  &:hover {
    background: var(--color-surface-panel);
    transform: translateY(-50%) scale(1.08);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.96);
  }
  
  &::after {
    content: '${props => props.$direction === 'left' ? '‹' : '›'}';
    font-weight: 700;
  }
`;

const PaginationDots = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  z-index: 10;
`;

const Dot = styled.button`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  border: none;
  padding: 0;
  background: ${props => props.$isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.3)'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  opacity: ${props => props.$isActive ? 1 : 0.5};
  
  &:hover {
    opacity: 0.8;
    transform: scale(1.2);
  }
  
  &:active {
    transform: scale(0.9);
  }
`;

const TitleContainer = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: color-mix(in srgb, var(--color-surface-panel) 85%, transparent);
  backdrop-filter: blur(25px) saturate(1.8);
  -webkit-backdrop-filter: blur(25px) saturate(1.8);
  padding: 10px 18px;
  border-radius: 4px;
  color: var(--color-text-primary);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.01em;
  z-index: 10;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  border: 1px solid var(--color-border-light);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;
